import React, { useEffect, useState } from 'react';
import { Slider } from 'office-ui-fabric-react';
import { DefaultButton, Separator, Spinner, SpinnerSize } from '@fluentui/react';
import { SavingsPlanType, SavingsPlanTypes } from '@/services/SavingsPlanService';
import { UserType } from '@/services/UserService';
import { SubscriptionType } from '@/services/SubscriptionService';
import { SavingsPlanUserIdentityType } from '@/services/SavingsPlanUserIdentityService';
import SuitabilityTestsService, { ISuitabilityTestVersion } from '@/services/SuitabilityTestsService';
import UserService from '@/services/UserService';
import { UsersSubscriptionType } from '@/services/UsersSubscriptionService';
import './SavingsPlans.sass';
import './FirstSteps.sass';
import { registerIcons } from '@uifabric/styling';
import { createIntl, createIntlCache } from '@formatjs/intl';
import Spanish from '@/translations/es.json';
//import { useHistory } from 'react-router-dom';
import Moment from "moment";
import { decimalFormatter } from '@/utils/numberFormatter';
import './FirstStepsServices.sass';

const cache = createIntlCache()
const intl = createIntl(
    {
        locale: 'es',
        messages: Spanish,
    },
    cache
);

registerIcons(
{
    icons:
    {
        'ironiaCancel': <i className="ironia-icon cross" />,
    }
});


const FirstStepsServices: React.FC<
{
    savingsPlan?: SavingsPlanType,
    subscription?: SubscriptionType,
    isSavingsPlanReady: boolean,
    publicVersion?: boolean,
    user: UserType,
    savingsPlanIsCompleted: boolean,
    isDisabled?: boolean,
    errorMessage?: string,
    onExperienceSampleSubscribe: () => void
}> = (
{
    savingsPlan,
    subscription,
    isSavingsPlanReady,
    publicVersion,
    user,
    savingsPlanIsCompleted,
    isDisabled,
    errorMessage,
    onExperienceSampleSubscribe
}) =>
{
    const valueFormat = (value: number) => `${value}%`;
    //const history = useHistory();
    const [error, setError] = useState({message: ""});
    const [suitabilityTestVersion, setSuitabilityTestVersion] = useState<ISuitabilityTestVersion>();
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
    const [usersSubscriptions, setUsersSubscriptions] = useState<UsersSubscriptionType[]>();
    const [hasSuitabilityTest, setHasSuitabilityTest] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);

    const getProgress = () =>
    {
        var progress = 0;
        if(publicVersion)
        {
            return progress;
        }

        if(isSavingsPlanReady)
        {
            progress += 25
        }

        if(progress > 25)
        {
            progress += 1
        }

        return progress;
    };

    useEffect(() =>
    {
        if(!loadingSubscriptions && user?.id !== undefined && usersSubscriptions === undefined && savingsPlan?.type !== undefined &&
            (savingsPlan.type !== SavingsPlanTypes.ExperienceSample))
        {
            setLoadingSubscriptions(true);
            UserService.getSuscriptions(user.id).then((subscriptionsStored: UsersSubscriptionType[]) =>
            {
                setIsLoaded(true);
                setUsersSubscriptions(subscriptionsStored);
                setLoadingSubscriptions(false);
            },
            (error) =>
            {
                setIsLoaded(true);
                setError(error);
                setLoadingSubscriptions(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() =>
    {
        if(!publicVersion && savingsPlan?.type !== undefined && savingsPlan.type !== SavingsPlanTypes.Experience)
        {
            SuitabilityTestsService.getLastSuitabilityTestVersion().then((suitabilityTestVersion: ISuitabilityTestVersion) =>
            {
                setSuitabilityTestVersion(suitabilityTestVersion);
            },
            (error) =>
            {
                setError(error);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() =>
    {
        if(suitabilityTestVersion !== undefined && savingsPlan?.savingsPlansUserIdentities !== undefined && savingsPlan.savingsPlansUserIdentities.length > 0)
        {
            var pendingSuitabilityTest = savingsPlan.savingsPlansUserIdentities.find((savingsPlanUserIdentity: SavingsPlanUserIdentityType) =>
            {
                if(savingsPlanUserIdentity.userIdentity === undefined)
                {
                    return true;
                }
                var currentDate = Moment();
                var birthdayDate = Moment(savingsPlanUserIdentity.userIdentity.birthday);
                var isMinor = currentDate.diff(birthdayDate, "years") < 18;

                if(isMinor)
                {
                    return false;
                }

                return (savingsPlanUserIdentity.userIdentity.suitabilityTests !== undefined
                    && savingsPlanUserIdentity.userIdentity.suitabilityTests.length > 0
                    && !savingsPlanUserIdentity.userIdentity.suitabilityTests.some(sT => sT.suitabilityTestVersionId === suitabilityTestVersion.id && sT.status === 'SIGNED'))
            });
            setHasSuitabilityTest(pendingSuitabilityTest === undefined);
        }
    }, [savingsPlan, suitabilityTestVersion]);


    const isDisabledConvenience = () =>
    {
        return publicVersion || savingsPlan?.type === undefined || savingsPlan.type === SavingsPlanTypes.ExperienceSample;
    };

    const isDisabledSuitability = () =>
    {
        return publicVersion || !savingsPlanIsCompleted || savingsPlan?.status === undefined || savingsPlan.status < 2;
    };

    const isDisabledInitialContribution = () =>
    {
        return publicVersion || !savingsPlanIsCompleted || savingsPlan?.status === undefined || savingsPlan.status < 2 || !hasSuitabilityTest;
    };


    const subscribe = () =>
    {
        setIsSubscribing(true);
        onExperienceSampleSubscribe();  
    };

    return(
        <div className="first-steps shadow">
            <div className="ms-Grid-col ms-sm12">
                <h3 style={{cursor: "default"}}>Da tus primeros pasos</h3>
                <Slider max={100} value={getProgress()} valueFormat={valueFormat} disabled={true} />
            </div>
            <div className="ms-Grid-col ms-sm12">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm12 ms-md3">
                        <div className={'step grey'}>
                            <div className="step-content">
                            {!(isSavingsPlanReady || isDisabled) || subscription === undefined ? (
                                <Spinner size={SpinnerSize.large} />
                            ) : (
                                <React.Fragment>
                                    <h4>{subscription.name}</h4>
                                    <h4>{decimalFormatter.format(subscription.price as number)} €/{intl.messages[subscription?.period || '']}</h4>
                                    <p>{subscription?.description}</p>
                                </React.Fragment>
                            )}

                            </div>
                            <div className="step-footer text-center">
                                {savingsPlan?.type !== undefined && savingsPlan.type === SavingsPlanTypes.ExperienceSample  &&
                                (
                                    <React.Fragment>
                                        <DefaultButton 
                                            className="button primary-button" 
                                            disabled={!isSavingsPlanReady || isSubscribing}
                                            onClick={() => subscribe()}
                                        >
                                            {isSubscribing && <Spinner size={SpinnerSize.small} className="button-spinner" />}
                                            Contratar
                                        </DefaultButton>
                                    </React.Fragment>
                                )}
                            </div>
                        </div>
                        <Separator className="separator hiddenSmUp" />
                    </div>
                    <div className="ms-Grid-col ms-sm12 ms-md3">
                        <div className={"step "+(isDisabledConvenience() ? " disabled" : "")}>
                            <div className="step-content">
                                <h4>Datos fiscales obligatorios</h4>
                                <p>
                                    Antes de poder operar debes rellenar un breve test para confirmar ante los organismos
                                    correspondientes que sabes qué estás haciendo. Recuerda que debes tener tu DNI y al menos una nómina a
                                    mano.
                                </p>
                            </div>
                            <Separator className="separator hiddenSmUp" />
                        </div>
                    </div>
                    {savingsPlan?.type !== undefined && savingsPlan.type === SavingsPlanTypes.ExperienceSample &&
                    (
                        <div className="ms-Grid-col ms-sm12 ms-md3">
                            <div className={"step" + (isDisabledSuitability() ? " disabled" : "")}>
                                <div className="step-content">
                                    <h4>Datos idoneidad de la inversión</h4>
                                    <p>
                                        Estos datos sólo los deberás rellenar si decides invertir a través de un gestor o un
                                        asesor. En ellos presentarás qué tipo de inversión deseas realizar.
                                    </p>
                                </div>

                            </div>
                            <Separator className="separator hiddenSmUp" />
                        </div>
                    )}
                    {savingsPlan?.type !== undefined && savingsPlan.type === SavingsPlanTypes.ExperienceSample &&
                    (
                        <div className="ms-Grid-col ms-sm12 ms-md3">
                            <div className={"step" + (isDisabledInitialContribution() ? " disabled" : "")}>
                                <div className="step-content">
                                    <h4>Aportación inicial</h4 >
                                    <p>Si seleccionas esta opción deberás indicar qué importe quieres invertir inicialmente.</p>
                                </div>
                            </div>
                            <Separator className="separator hiddenSmUp" />
                        </div>
                    )}
                </div>
                <span className='error'>{errorMessage}</span>
            </div>
            
        </div>
    )}

export default FirstStepsServices;